import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { plansService, PlansRecord } from "@/services";
import {
  AmexIcon,
  CancelIcon,
  EloIcon,
  HipercardIcon,
  MastercardIcon,
  PixIcon,
  PrivacyIcon,
  SupportIcon,
  VerifiedIcon,
  VisaIcon,
} from "@/svgs";
import "./Plans.css";
import { PaypalIcon } from "@/svgs/PaypalIcon";

const PlansScreen: React.FC = () => {
  const navigate = useNavigate();

  const [plans, setPlans] = useState<PlansRecord[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const data = await plansService.getActivePlans();
      setPlans(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (value: number) => {
    return (value / 100).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const getPlanDurationLabel = (plan: PlansRecord) => {
    if (plan.name.toLowerCase().includes("dia")) {
      return plan.name.includes("7") ? "/ 7 dias" : "/ 1 dia";
    }
    if (plan.durationMonths === 1) return "/ mês";
    return `/ ${plan.durationMonths} meses`;
  };

  const orderedPlans = useMemo(() => {
    return [...plans].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [plans]);

  const handleSelect = (planId: string) => {
    setSelectedPlan(planId);
    navigate(`/checkout/${planId}`);
  };

  if (loading) {
    return <div className="plans-loading">Carregando planos...</div>;
  }

  return (
    <div className="plans-container-wrapper">
      <header className="plans-header-section">
        <h1 className="plans-main-title">
          Escolha seu plano e comece a se conectar
        </h1>
        <p className="plans-subtitle">
          Mais recursos, mais conexões, mais chances de encontrar alguém
          especial.
        </p>

        <div className="features-badges">
          <span>
            <VerifiedIcon color="#1b4322" /> Ambiente seguro
          </span>
          <span>
            <VerifiedIcon color="#1b4322" /> Perfis verificados
          </span>
          <span>
            <PrivacyIcon color="#1b4322" /> Privacidade garantida
          </span>
          <span>
            <VerifiedIcon color="#1b4322" /> Cancele quando quiser
          </span>
        </div>
      </header>

      <div className="plans-grid-layout">
        {orderedPlans.map((plan) => {
          const hasDiscount = !!plan.durationMonths && plan.durationMonths >= 3;

          return (
            <div
              key={plan.id}
              className={`premium-plan-card 
                ${plan.isHighlight ? "is-popular" : ""}
                ${selectedPlan === plan.id ? "is-selected" : ""}
              `}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(plan.id);
              }}
            >
              {plan.isHighlight && (
                <div className="badge-highlight-chosen">MAIS ESCOLHIDO</div>
              )}

              {hasDiscount && (
                <div className="badge-discount-percent">10% OFF</div>
              )}

              <div className="card-main-content-wrapper">
                <div className="card-header-info">
                  <h2>{plan.name}</h2>
                  <p className="access-info-text">
                    {plan.description || "Acesso total por 24 horas"}
                  </p>
                </div>

                <div className="card-price-section">
                  <span className="price-value">{formatPrice(plan.price)}</span>
                  <span className="price-period">
                    {getPlanDurationLabel(plan)}
                  </span>
                </div>
              </div>

              <ul className="card-features-list">
                {plan.features?.map((feature, i) => (
                  <li key={i}>
                    <span className="check-icon">✓</span> {feature}
                  </li>
                ))}
              </ul>

              <button className="btn-select-premium">Selecionar plano</button>

              <span className="mobile-arrow">❯</span>
            </div>
          );
        })}
      </div>

      <footer className="plans-footer-trust">
        <div className="plans-trust-item">
          <div className="trust-icon-wrapper">
            <VerifiedIcon />
          </div>
          <div>
            <h4>Pagamento 100% seguro</h4>
            <p>Ambiente criptografado e protegido</p>
          </div>
        </div>
        <div className="plans-trust-item">
          <div className="plans-trust-icon-wrapper">
            <CancelIcon />
          </div>
          <div>
            <h4>Cancele quando quiser</h4>
            <p>Sem fidelidade ou taxas ocultas</p>
          </div>
        </div>
        <div className="plans-trust-item">
          <div className="plans-trust-icon-wrapper">
            <SupportIcon />
          </div>
          <div>
            <h4>Suporte dedicado</h4>
            <p>Estamos aqui para ajudar você</p>
          </div>
        </div>
      </footer>

      <div className="payment-gateways-area">
        <h3>Formas de pagamento aceitas</h3>
        <div className="gateways-logos">
          <div className="gateway-card brand-visa">
            <VisaIcon />
          </div>

          <div className="gateway-card">
            <MastercardIcon />
          </div>

          <div className="gateway-card brand-elo">
            <EloIcon />
          </div>

          <div className="gateway-card brand-hipercard">
            <HipercardIcon />
          </div>

          <div className="gateway-card brand-amex">
            <AmexIcon />
          </div>

          <div className="gateway-card brand-paypal">
            <PaypalIcon />
          </div>

          <div className="gateway-card brand-pix">
            <PixIcon />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlansScreen;
